import type { LDMultiKindContext } from 'launchdarkly-react-client-sdk';
import type { CurrentUser } from 'stores/useUserStore';

export type FeatureFlagSet = KnownLDFeatureFlags;

export type LDFeatureFlagsKey = keyof KnownLDFeatureFlags;

let featureFlagsOverride: Partial<FeatureFlagSet>;

/**
 * We use a shared static key for anonymous users.
 * This is to prevent the SDK from automatically generating a new key for each anonymous user.
 *
 * Source: https://docs.launchdarkly.com/home/users/anonymous-users#tracking-anonymous-users-with-a-shared-key
 */
export const ANONYMOUS_USER_KEY = 'anonymous_user';

/**
 * Active context kinds should match this - https://app.launchdarkly.com/edealer/dev/contexts/kinds
 */
export enum LDContextKinds {
  rooftop = 'rooftop',
  user = 'user',
}

/**
 * Formats a user object for identifying the user with LaunchDarkly's feature flags.
 * The data provided will dictate what sort of flags will be set for this user
 * See this for more info: https://docs.launchdarkly.com/sdk/features/user-config#javascript
 *
 * @param {CurrentUser} user - The user to be identified. If no user id is provided, then an anonymous
 * configuration will be returned
 */
export const createLaunchDarklyContext = (user: CurrentUser): LDMultiKindContext => ({
  kind: 'multi',
  user: {
    admin: !user?.group?.id,
    anonymous: !user?.id,
    key: user?.group?.id || user?.id || ANONYMOUS_USER_KEY,
    name: user?.group?.name?.value || user?.displayName || ANONYMOUS_USER_KEY,
    platform: 'web',
    userId: user?.id,
    whiteLabelId: user?.whiteLabel?.id || user?.group?.whiteLabel?.id || '',
  },
});

/**
 * A helper method to set feature flags for Storybook components that require them
 *
 * @see hasFeatureFlag
 * @example
 * // someComponent.stories.tsx
 * setFeatureFlagsOverride({ 'rooftopPackageEnabled': true });
 * const Template = () => (
 *   <StorybookTemplate>
 *     <Component />
 *   </StorybookTemplate>
 * );
 */
export const setFeatureFlagsOverride = (featureFlags: Partial<FeatureFlagSet>) => {
  featureFlagsOverride = featureFlags;
};

export const getFeatureFlagsOverride = () => featureFlagsOverride;
